import { Tag } from 'antd';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import { ContainerState } from 'containers/ProductDetailsPage/types';
import React, { memo, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import translations from 'translations';
import { Language } from 'types/schema';
import { getLimitedTimeOfferPercent } from 'utils/discount';
import utilMessages from 'utils/messages';
import Image from '../Image/Image';
import Favorite, { Favorite as FavoriteContainer, FavoriteIcon } from '../MarketItem/Favorite';
import Heart from '../MarketItem/Heart.svg';
import PlayVideoButton from './_icons/PlayVideoButton.svg';
import ProductImagesModal from './ProductImagesModal';

interface ProductGalleryProps {
  lang: Language;
  loggedIn: boolean;
  componentPermissions: {
    addFavoritePermission: string;
    addToCartPermission: string;
  };
  permissions: string[];
  product: ContainerState['product'];
  onToggleFavorite: () => void;
  gotoSignIn: () => void;
}

const VideoElement = styled.video`
  pointer-events: none;
  @media (max-width: 767px) {
    pointer-events: default;
  }
  width: 100%;
  max-height: 386px;
  height: 100%;
  border-radius: 8px;
`;

const ThumbnailWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
  padding-bottom: 10px;
  overflow: auto;
`;

const ThumbnailItem = styled(SwiperSlide)`
  height: 52px !important;
  width: 52px !important;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  ${({ active }) =>
    active &&
    `
    border: 1px solid #43A047
  `}
`;

const VideoThumbnailWrapper = styled.div`
  position: relative;
  height: 100%;
`;

const VideoThumbnailButton = styled(SVGIcon)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const TagStyled = styled(Tag)`
  background: #ff4852;
  border: none;
  border-radius: 0;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  color: #ffffff;
  font-size: 16px;
  margin-right: 0;
  padding: 5px;
  z-index: 1;

  &.new {
    background: #40a9ff;
  }

  &.book-in-advance {
    background: #e5c03c;
  }
`;

const TagContainer = styled.div`
  position: absolute;
  bottom: 12px;
  right: 0;
  flex-direction: column;
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
`;

const DiscountTag = styled(Tag)`
  background: #ff4852;
  border: none;
  border-radius: 0;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  color: #ffffff;
  font-size: 16px;
  margin-right: 0;
  padding: 5px;
  position: absolute;
  top: 12px;
  left: 0;
  z-index: 100;
`;

const OutOfStockOverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.3);
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OutOfStockOverlayContent = styled.div`
  background: #000;
  opacity: 0.5;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: #ffffff;
`;

const ProductGallery = ({
  product,
  loggedIn,
  componentPermissions,
  permissions,
  onToggleFavorite,
  gotoSignIn,
}: ProductGalleryProps) => {
  const { allowPurchase, originalPrice, price, category, loading, name, tags, isFavorite, medias, priceV2, inStock } =
    product;
  const itemDiscountExists = originalPrice > price;

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null as any);
  const videoRef = useRef(null as any);

  const handleManipulateVideo = useCallback((currentIndex: number): void => {
    if (videoRef.current) {
      if (currentIndex === 0) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, []);

  const handleClickThumbnail = useCallback(
    (index: number): void => {
      handleManipulateVideo(index);
      setActiveIndex(index);
      if (swiperRef.current?.slideTo) {
        swiperRef.current.slideTo(index, 500);
      }
    },
    [handleManipulateVideo],
  );

  const handleSlideChange = useCallback(
    (index: number): void => {
      handleManipulateVideo(index);
      setActiveIndex(index);
    },
    [handleManipulateVideo],
  );

  return (
    <>
      <div style={{ position: 'relative' }}>
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          className="mySwiper"
          onSlideChange={(swiper) => handleSlideChange(swiper.activeIndex)}
        >
          {loading ? (
            <Image
              zoomIn
              responsive
              src={getImageByCategory(category)}
              alt={name ?? translations(utilMessages.productDetailImage)}
              width="386px"
              height="386px"
              nopadding
            />
          ) : (
            medias?.map((media, index) => (
              <SwiperSlide
                key={index}
                onClick={() => {
                  if (window.innerWidth < 768) return;
                  setIsOpen(true);
                }}
              >
                {media?.type === 'VIDEO' ? (
                  <VideoElement
                    ref={videoRef}
                    autoPlay
                    muted
                    controls
                    style={{ width: '100%', maxHeight: '386px', height: '100%' }}
                    src={media?.url || ''}
                  />
                ) : (
                  <>
                    <Image
                      zoomIn={false}
                      responsive
                      src={!loading ? media?.url || getImageByCategory(category) : ''}
                      alt={name ?? translations(utilMessages.productDetailImage)}
                      width="386px"
                      height="386px"
                      borderRadius="8px"
                      nopadding
                    />
                    {!inStock && (
                      <OutOfStockOverlayContainer>
                        <OutOfStockOverlayContent>
                          <FormattedMessage {...utilMessages.outStock} />
                        </OutOfStockOverlayContent>
                      </OutOfStockOverlayContainer>
                    )}
                  </>
                )}
              </SwiperSlide>
            ))
          )}
        </Swiper>
        {permissions.includes(componentPermissions.addFavoritePermission) && allowPurchase && (
          <Favorite
            renderAsImage={false}
            onClick={onToggleFavorite}
            alignto="right"
            favorite={isFavorite}
            isShowBorder={true}
            isProductDetail={true}
          />
        )}
        {!loggedIn && allowPurchase && (
          <FavoriteContainer
            type="secondary"
            shape="circle"
            onClick={gotoSignIn}
            isShowBorder={true}
            isProductDetail={true}
          >
            <FavoriteIcon renderAsImg={false} src={Heart} aria-label="Add to favorite" />
          </FavoriteContainer>
        )}
        {itemDiscountExists && !loading && (
          <DiscountTag>{getLimitedTimeOfferPercent(priceV2?.originalPrice || 0, priceV2?.price || 0)}</DiscountTag>
        )}
        <TagContainer>
          {(tags || []).includes('NEW_PRODUCTS') && !loading && (
            <div>
              <TagStyled className="new">
                <FormattedMessage {...utilMessages.newTag} />
              </TagStyled>
            </div>
          )}
        </TagContainer>
      </div>
      {Number(medias?.length) > 1 && (
        <ThumbnailWrapper>
          {medias?.map((media, index) => (
            <ThumbnailItem
              onClick={() => handleClickThumbnail(index)}
              key={index}
              style={{ width: 'fit-content' }}
              active={index === activeIndex}
            >
              {media?.type === 'VIDEO' ? (
                <VideoThumbnailWrapper>
                  <video style={{ width: '100%', height: '100%' }} src={media?.url || ''} />
                  <VideoThumbnailButton src={PlayVideoButton} />
                </VideoThumbnailWrapper>
              ) : (
                <Image
                  zoomIn={false}
                  responsive
                  src={!loading ? media?.url || getImageByCategory(category) : ''}
                  alt={name ?? translations(utilMessages.productDetailImage)}
                  nopadding
                />
              )}
            </ThumbnailItem>
          ))}
        </ThumbnailWrapper>
      )}
      {isOpen && (
        <ProductImagesModal
          product={product}
          open={isOpen}
          activeIndexProp={activeIndex}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default memo(ProductGallery);
